import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { Instagram } from 'lucide-react'
import { cn } from '@/lib/utils'
import { SectionHeading } from '@/components/generals/section-heading'
import { buttonClass } from '@/components/generals/cta-buttons'
import { instagramTiles } from '@/constants/content'
import { contact } from '@/constants/site'

export const InstagramSection: FC = (): ReactNode => {
    return (
        <section
            id='instagram'
            aria-labelledby='instagram-title'
            className='bg-background py-20 sm:py-24 lg:py-28'
        >
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <SectionHeading
                    id='instagram-title'
                    eyebrow='Follow our work'
                    title='See our looks on Instagram'
                    description='On our Instagram profile you can view our makeups, nail arts, hair designs and much more.'
                />

                <div className='mx-auto mt-10 flex max-w-2xl flex-col items-center gap-4 rounded-3xl border border-gold/30 bg-white p-5 text-center shadow-sm sm:flex-row sm:p-6 sm:text-left'>
                    <span className='shrink-0 rounded-full bg-[conic-gradient(from_210deg,#f7df8e,#d4af37,#c2577a,#5e1b3f,#f7df8e)] p-[3px]'>
                        <span className='flex size-16 items-center justify-center rounded-full border-2 border-white bg-plum-deep font-heading text-2xl font-bold text-gold-light'>
                            A
                        </span>
                    </span>

                    <div className='min-w-0 flex-1'>
                        <p className='break-all font-heading text-base font-semibold text-plum'>
                            {contact.instagramHandle}
                        </p>

                        <p className='mt-1 text-sm text-muted-foreground'>
                            Bridal makeups, party looks, nail arts and hair designs by Anushka Beauty Parlour.
                        </p>
                    </div>

                    <a
                        href={contact.instagramUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className={buttonClass('plum', 'md', 'w-full shrink-0 sm:w-auto')}
                    >
                        <Instagram className='size-4' aria-hidden='true' />
                        Follow us
                    </a>
                </div>

                <ul className='mt-10 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-6'>
                    {instagramTiles.map(tile => (
                        <li key={tile.label}>
                            <a
                                href={contact.instagramUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                aria-label={`See more ${tile.label.toLowerCase()} on our Instagram`}
                                className='group relative block aspect-square overflow-hidden rounded-2xl bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
                            >
                                <Image
                                    src={tile.image}
                                    alt={tile.alt}
                                    fill
                                    sizes='(min-width: 1024px) 16vw, (min-width: 768px) 30vw, 48vw'
                                    className={cn('object-cover transition-transform duration-500 group-hover:scale-110 motion-reduce:transition-none motion-reduce:group-hover:scale-100', tile.className)}
                                />

                                <span aria-hidden='true' className='absolute inset-0 bg-gradient-to-t from-plum-deep/85 via-plum-deep/10 to-transparent' />

                                <span className='absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 p-3 text-ivory'>
                                    <span className='text-sm font-medium leading-tight'>
                                        {tile.label}
                                    </span>

                                    <Instagram className='size-4 shrink-0 text-gold-light opacity-80 transition-opacity group-hover:opacity-100' aria-hidden='true' />
                                </span>
                            </a>
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    )
}
